import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchTransactions,
  modifyTransaction,
  removeTransaction
} from '../store/slices/transactionSlice'
import Swal from 'sweetalert2'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import {
  ArrowDownUp,
  ChevronLeft,
  ChevronRight,
  Edit,
  Search,
  Trash2,
  X,
  Filter,
  Download,
  ChevronsRight,
  ChevronsLeft,
  Layers
} from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import ReactDOMServer from 'react-dom/server'
import { createRoot } from 'react-dom/client'
import EnhancedDateFilter from './EnhancedDateFilter'
import ExportExcelExpense from './ExportExcelExpense'

const ExpenseDirection = () => {
  const dispatch = useDispatch()
  const { activeSociety, theme, gradient } = useSidebarContext()
  const transactionsData = useSelector(state => state.transactions)
  const activeUser = useSelector(state => state.auth.activeUser)
  const [allFilteredTransactions, setAllFilteredTransactions] = useState([])

  // State management
  const [filters, setFilters] = useState({
    transactionType: '',
    amount: '',
    description: '',
    startDate: '',
    endDate: '',
    transactionDirection: 'Expense'
  })
  const [sortOrder, setSortOrder] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [dateRange, setDateRange] = useState([null, null])
  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [totalFilteredAmount, setTotalFilteredAmount] = useState(0)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [isTableScrollable, setIsTableScrollable] = useState(true)

  const isEmployee = activeUser?.role === 'Employee'

  // Handle window resize for responsive table
  useEffect(() => {
    const handleResize = () => {
      setIsTableScrollable(window.innerWidth < 1024)
    }

    handleResize() // Check on initial load
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Update the useEffect for filtering transactions
  useEffect(() => {
    if (activeSociety?._id) {
      dispatch(fetchTransactions({ societyId: activeSociety._id }))
    }
  }, [activeSociety, dispatch])

  // Extract the raw transactions array from the nested structure
  const rawTransactions = useMemo(() => {
    if (transactionsData?.transactions?.data?.transactions) {
      return transactionsData.transactions.data.transactions
    }
    return []
  }, [transactionsData])
  // console.log('rawTransactions', rawTransactions)s
  // Update the useEffect for filtering transactions
  useEffect(() => {
    // If rawTransactions are available, filter them
    if (Array.isArray(rawTransactions) && rawTransactions.length > 0) {
      const filtered = rawTransactions.filter(transaction => {
        // Only include Income transactions
        if (transaction.transaction_direction !== 'Expense') {
          return false
        }

        // Only filter by description if search term is provided
        if (
          filters.description &&
          !transaction.description
            ?.toLowerCase()
            .includes(filters.description.toLowerCase())
        ) {
          return false
        }

        // Only filter by date if both dates are provided
        if (filters.startDate && filters.endDate) {
          const transactionDate = new Date(transaction.transaction_date)
          const startDate = new Date(filters.startDate)
          startDate.setHours(0, 0, 0, 0)
          const endDate = new Date(filters.endDate)
          endDate.setHours(23, 59, 59, 999)
          if (transactionDate < startDate || transactionDate > endDate) {
            return false
          }
        }

        // Only filter by transaction type if specified
        if (
          filters.transactionType &&
          transaction.transaction_type !== filters.transactionType
        ) {
          return false
        }

        return true
      })

      // Store all filtered transactions for pagination calculation
      setAllFilteredTransactions(filtered)

      const sorted = [...filtered].sort((a, b) => {
        const dateA = new Date(a.transaction_date)
        const dateB = new Date(b.transaction_date)
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      })

      // Apply pagination
      const startIndex = (currentPage - 1) * itemsPerPage
      const paginatedTransactions = sorted.slice(
        startIndex,
        startIndex + itemsPerPage
      )

      setFilteredTransactions(paginatedTransactions)

      // Calculate total for all filtered transactions (before pagination)
      const total = filtered.reduce((sum, transaction) => {
        return sum + (Number(transaction.amount) || 0)
      }, 0)

      setTotalFilteredAmount(total)
    } else {
      setFilteredTransactions([])
      setAllFilteredTransactions([])
      setTotalFilteredAmount(0)
    }
  }, [rawTransactions, filters, sortOrder, currentPage, itemsPerPage])

  const totalIncomeCount = useMemo(() => {
    if (Array.isArray(rawTransactions)) {
      return rawTransactions.filter(t => t.transaction_direction === 'Expense')
        .length
    }
    return 0
  }, [rawTransactions])

  const actualFilteredCount = useMemo(() => {
    return allFilteredTransactions.length
  }, [allFilteredTransactions])

  const totalExpenses = useMemo(() => {
    if (transactionsData?.transactions?.data?.summary?.totalExpense) {
      return transactionsData.transactions.data.summary.totalExpense
    }
    return 0
  }, [transactionsData])

  // Group transactions by type for calculations
  const salaryExpenses = filteredTransactions.filter(
    t =>
      t.transaction_type === 'Salary Payment' &&
      t.transaction_direction === 'Expense'
  )

  const dealerExpenses = filteredTransactions.filter(
    t =>
      t.transaction_type === 'Dealer Commission' &&
      t.transaction_direction === 'Expense'
  )

  const totalSalary = calculateTotal(salaryExpenses)
  const totalDealer = calculateTotal(dealerExpenses)

  const handleNextPage = () => {
    const totalPages = Math.ceil(actualFilteredCount / itemsPerPage)
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleFirstPage = () => {
    setCurrentPage(1)
  }

  const handleLastPage = () => {
    const totalPages = Math.ceil(actualFilteredCount / itemsPerPage)
    setCurrentPage(totalPages > 0 ? totalPages : 1)
  }

  const handleSortOrderChange = () => {
    setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'))
  }

  // Utility functions
  function calculateTotal (transactions) {
    return transactions.reduce((sum, t) => sum + Number(t.amount || 0), 0)
  }

  const clearFilters = () => {
    setFilters({
      transactionType: '',
      description: '',
      transactionDirection: 'Expense',
      amount: '',
      startDate: '',
      endDate: ''
    })
    setDateRange([null, null])
    setCurrentPage(1)
  }

  const getTransactionTypeColor = (transactionType, direction) => {
    let baseStyle = 'transition-colors duration-300 border-l-4 border-red-500 '

    const transactionStyles = {
      'Full Payment': 'bg-green-500/15 hover:bg-green-500/25',
      'Partial Payment': 'bg-green-500/15 hover:bg-green-500/25',
      'Installment Payment': 'bg-purple-500/15 hover:bg-purple-500/25',
      'Salary Payment': 'bg-yellow-500/15 hover:bg-yellow-500/25',
      'Expense Payment': 'bg-red-500/15 hover:bg-red-500/25',
      'Resell Payment': 'bg-indigo-500/15 hover:bg-indigo-500/25',
      'Dealer Commission': 'bg-orange-500/15 hover:bg-orange-500/25',
      'Booking Full Payment': 'bg-cyan-500/15 hover:bg-cyan-500/25',
      'Booking Installment Payment': 'bg-cyan-500/15 hover:bg-cyan-500/25',
      'Transfer Fee': 'bg-pink-500/15 hover:bg-pink-500/25'
    }

    return (
      baseStyle +
      (transactionStyles[transactionType] ||
        'bg-teal-500/15 hover:bg-teal-500/25')
    )
  }

  const handleEdit = transaction => {
    if (isEmployee) return
    const dateRef = React.createRef()

    const CustomDialog = () => {
      const [dateValue, setDateValue] = React.useState(
        new Date(transaction.transaction_date)
      )
      return (
        <div className='max-w-3xl mx-auto p-6'>
          <div
            className={`${gradient} p-8 rounded-xl shadow-2xl border ${theme.tailwind.border19}`}
          >
            <h2 className={`text-3xl font-bold ${theme.tailwind.text9} mb-6`}>
              Edit Transaction
            </h2>
            <div className='grid grid-cols-2 gap-6'>
              {[
                {
                  id: 'amount',
                  label: 'Amount',
                  type: 'number',
                  value: transaction.amount
                },
                {
                  id: 'transaction_type',
                  label: 'Transaction Type',
                  type: 'text',
                  value: transaction.transaction_type
                },
                {
                  id: 'transaction_direction',
                  label: 'Transaction Direction',
                  type: 'text',
                  value: 'Expense',
                  disabled: true
                },
                {
                  id: 'payment_method',
                  label: 'Payment Method',
                  type: 'text',
                  value: transaction.payment_method
                },
                {
                  id: 'status',
                  label: 'Status',
                  type: 'text',
                  value: transaction.status
                },
                {
                  id: 'description',
                  label: 'Description',
                  type: 'text',
                  value: transaction.description
                },
                {
                  id: 'receipt_no',
                  label: 'Receipt No',
                  type: 'text',
                  value: transaction.receipt_no
                }
              ].map(field => (
                <div key={field.id} className='relative'>
                  <label
                    className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                  >
                    {field.label}
                  </label>
                  <input
                    id={field.id}
                    type={field.type}
                    className={`w-full px-4 py-2.5 rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.bg5} ${theme.tailwind.text9}
                    placeholder-slate-400 ${theme.tailwind.focus6} focus:ring-2 ${theme.tailwind.focusRing5}
                    focus:ring-opacity-50 transition-all duration-200 ease-in-out`}
                    defaultValue={field.value}
                    disabled={field.disabled}
                  />
                </div>
              ))}
              <div className='relative'>
                <label
                  className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                >
                  Transaction Date
                </label>
                <DatePicker
                  selected={dateValue}
                  onChange={date => {
                    setDateValue(date)
                    dateRef.current = date
                  }}
                  dateFormat='dd/MM/yyyy'
                  placeholderText='DD/MM/YYYY'
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode='select'
                  className={`w-full px-4 py-2.5 rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.bg5} ${theme.tailwind.text9}
                  placeholder-slate-400 ${theme.tailwind.focus6} focus:ring-2 ${theme.tailwind.focusRing5}
                  focus:ring-opacity-50 transition-all duration-200 ease-in-out`}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    Swal.fire({
      html: ReactDOMServer.renderToString(<CustomDialog />),
      showCancelButton: true,
      confirmButtonText: 'Save Changes',
      cancelButtonText: 'Cancel',
      width: 'auto',
      background: '#1E293B',
      customClass: {
        popup: `${gradient} rounded-xl ${theme.tailwind.text}`,
        confirmButton: `${theme.tailwind.bg49} ${theme.tailwind.bgHover23} ${theme.tailwind.text9} px-6 py-2.5 rounded-lg transition-colors duration-200`,
        cancelButton: `bg-red-500 hover:bg-red-600 ${theme.tailwind.text9} px-6 py-2.5 rounded-lg transition-colors duration-200`,
        title: `text-2xl font-bold ${theme.tailwind.text9} mb-4`,
        container: 'backdrop-blur-sm'
      },
      didOpen: () => {
        const container = document.querySelector('.swal2-html-container')
        if (container) {
          // Clear any previous content
          container.innerHTML = ''

          // Create root and render the component
          const root = createRoot(container)
          root.render(<CustomDialog />)

          // Save root instance for cleanup
          container._rootInstance = root
        }
      },
      willClose: () => {
        const container = document.querySelector('.swal2-html-container')
        if (container && container._rootInstance) {
          // Unmount the component
          container._rootInstance.unmount()
        }
      },
      preConfirm: () => {
        return {
          transaction_date: dateRef.current
            ? dateRef.current.toISOString()
            : transaction.transaction_date,
          amount: document.getElementById('amount').value,
          transaction_type: document.getElementById('transaction_type').value,
          transaction_direction: 'Expense', // Always set to Expense
          payment_method: document.getElementById('payment_method').value,
          status: document.getElementById('status').value,
          description: document.getElementById('description').value,
          receipt_no: document.getElementById('receipt_no').value
        }
      }
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(
          modifyTransaction({
            id: transaction._id,
            transactionData: {
              ...transaction,
              ...result.value
            }
          })
        )

        Swal.fire({
          title: 'Success!',
          text: 'Transaction has been updated successfully',
          icon: 'success',
          confirmButtonColor: '#10B981',
          background: '#1E293B',
          color: '#5EEAD4',
          confirmButtonText: 'Done',
          customClass: {
            popup: `${gradient} rounded-xl shadow-xl`,
            confirmButton: `${theme.tailwind.bgHover23} transition-colors px-6 py-2.5 rounded-lg`,
            title: `text-xl font-bold ${theme.tailwind.text9}`
          }
        })
      }
    })
  }

  const handleDelete = transactionId => {
    if (isEmployee) return
    Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#1E293B',
      customClass: {
        popup: `${gradient} rounded-xl ${theme.tailwind.text}`,
        confirmButton: `${theme.tailwind.bg49} ${theme.tailwind.bgHover23} ${theme.tailwind.text9} px-6 py-2.5 rounded-lg transition-colors duration-200`,
        cancelButton: `bg-red-500 hover:bg-red-600 ${theme.tailwind.text9} px-6 py-2.5 rounded-lg transition-colors duration-200`,
        title: `text-2xl font-bold ${theme.tailwind.text9} mb-4`,
        container: 'backdrop-blur-sm'
      }
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(removeTransaction(transactionId))

        Swal.fire({
          title: 'Deleted!',
          text: 'The transaction has been deleted.',
          icon: 'success',
          confirmButtonColor: '#10B981',
          background: '#1E293B',
          color: '#5EEAD4',
          confirmButtonText: 'Done',
          customClass: {
            popup: `${gradient} rounded-xl shadow-xl`,
            confirmButton: `${theme.tailwind.bgHover23} transition-colors px-6 py-2.5 rounded-lg`,
            title: `text-xl font-bold ${theme.tailwind.text9}`
          }
        })
      }
    })
  }

  const renderPagination = () => {
    const totalPages = Math.ceil(actualFilteredCount / itemsPerPage)
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
    let pageNumbers = []
    if (totalPages <= 7) {
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    } else {
      if (currentPage <= 3) {
        pageNumbers = [1, 2, 3, 4, 5, '...', totalPages]
      } else if (currentPage >= totalPages - 2) {
        pageNumbers = [
          1,
          '...',
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        ]
      } else {
        pageNumbers = [
          1,
          '...',
          currentPage - 1,
          currentPage,
          currentPage + 1,
          '...',
          totalPages
        ]
      }
    }

    return (
      <div className='flex flex-col md:flex-row items-center justify-between gap-4 px-3 py-4'>
        <div className='flex items-center space-x-1 sm:space-x-2 w-full md:w-auto order-2 md:order-1 justify-center md:justify-start'>
          <button
            onClick={handleFirstPage}
            disabled={currentPage === 1 || totalPages === 0}
            className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300 ${
              currentPage === 1
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : `${theme.tailwind.bg5} ${theme.tailwind.text9} ${theme.tailwind.bgHover24}`
            }`}
            aria-label='First page'
          >
            <ChevronsLeft className='h-5 w-5' />
          </button>

          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || totalPages === 0}
            className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300 ${
              currentPage === 1 || totalPages === 0
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : `${theme.tailwind.bg5} ${theme.tailwind.text9} ${theme.tailwind.bgHover24}`
            }`}
            aria-label='Previous page'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>

          <div className='flex items-center space-x-1'>
            {pageNumbers.map((pageNum, index) =>
              pageNum === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className={`px-1 sm:px-2 ${theme.tailwind.texting2}`}
                >
                  ...
                </span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`flex items-center justify-center h-9 w-9 rounded-lg text-sm font-medium transition-all duration-300 ${
                    pageNum === currentPage
                      ? `${theme.tailwind.bg13} ${theme.tailwind.text9} shadow-xl scale-105`
                      : `${theme.tailwind.bg47} ${theme.tailwind.text9} ${theme.tailwind.bgHover24}`
                  }`}
                >
                  {pageNum}
                </button>
              )
            )}
          </div>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300 ${
              currentPage === totalPages || totalPages === 0
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : `${theme.tailwind.bg5} ${theme.tailwind.text9} ${theme.tailwind.bgHover24}`
            }`}
            aria-label='Next page'
          >
            <ChevronRight className='h-5 w-5' />
          </button>

          <button
            onClick={handleLastPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300 ${
              currentPage === totalPages || totalPages === 0
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : `${theme.tailwind.bg5} ${theme.tailwind.text9} ${theme.tailwind.bgHover24}`
            }`}
            aria-label='Last page'
          >
            <ChevronsRight className='h-5 w-5' />
          </button>
        </div>

        <div className='flex items-center space-x-3'>
          <span
            className={`text-xs sm:text-sm ${theme.tailwind.texting2} whitespace-nowrap`}
          >
            Showing{' '}
            <span className={`font-medium ${theme.tailwind.text9}`}>
              {actualFilteredCount > 0
                ? Math.min(
                    (currentPage - 1) * itemsPerPage + 1,
                    actualFilteredCount
                  )
                : 0}
            </span>{' '}
            to{' '}
            <span className={`font-medium ${theme.tailwind.text9}`}>
              {Math.min(currentPage * itemsPerPage, actualFilteredCount)}
            </span>{' '}
            of{' '}
            <span className={`font-medium ${theme.tailwind.text9}`}>
              {actualFilteredCount}
            </span>{' '}
            entries
          </span>

          <select
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value))
              setCurrentPage(1) // Reset to first page when changing items per page
            }}
            className={`${theme.tailwind.bg5} border ${theme.tailwind.border13} ${theme.tailwind.text9} rounded-lg px-2 py-1 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 ${theme.tailwind.focusRing5} ${theme.tailwind.focus6} text-xs sm:text-sm w-full sm:w-auto`}
          >
            {[10, 20, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize} per page
              </option>
            ))}
          </select>
        </div>
      </div>
    )
  }

  // Render transaction card for mobile view
  const renderTransactionCard = transaction => (
    <div
      key={transaction._id}
      className={`p-4 mb-4 rounded-lg shadow-md ${getTransactionTypeColor(
        transaction.transaction_type,
        transaction.transaction_direction
      )}`}
    >
      <div className='flex justify-between mb-2'>
        <div>
          <span
            className={`px-2 py-1 text-xs font-medium rounded-md ${'bg-red-700/70 text-red-300'}`}
          >
            {transaction.transaction_direction}
          </span>
          <span
            className={`ml-2 px-2 py-1 text-xs font-medium rounded-md ${theme.tailwind.bg48} ${theme.tailwind.text9}`}
          >
            {transaction.transaction_type}
          </span>
        </div>
        <span
          className={`font-medium ${
            transaction.transaction_direction === 'Income'
              ? `text-emerald-400`
              : 'text-red-400'
          }`}
        >
          {transaction.transaction_direction === 'Income' ? '+' : '-'}
          {transaction.amount?.toLocaleString() || '0'} PKR
        </span>
      </div>

      <div className='grid grid-cols-2 gap-2 text-sm'>
        <div>
          <p className={`${theme.tailwind.texting2}`}>Name:</p>
          <p className='font-medium text-slate-200'>
            {transaction.transaction_type === 'Salary Payment'
              ? transaction.employee_id?.name || <i>No Employee</i>
              : transaction.transaction_type === 'Dealer Commission'
              ? transaction.booking_id?.dealer_name || <i>No Dealer</i>
              : transaction.customer_id?.name ||
                (transaction.customer_info &&
                  transaction.customer_info[0]?.name) || <i>No user</i>}
          </p>
        </div>

        <div>
          <p className={`${theme.tailwind.texting2}`}>Plot No:</p>
          <p className='font-medium text-slate-200'>
            {transaction.plot_id?.plot_number ||
              (transaction.plot_info &&
                transaction.plot_info[0]?.plot_number) || <i>N/A</i>}
          </p>
        </div>

        <div>
          <p className={`${theme.tailwind.texting2}`}>Date:</p>
          <p className='font-medium text-slate-200'>
            {new Date(transaction.transaction_date).toLocaleDateString()}
          </p>
        </div>

        <div>
          <p className={`${theme.tailwind.texting2}`}>Method:</p>
          <p className='font-medium text-slate-200'>
            {transaction.payment_method || '-'}
          </p>
        </div>

        <div className='col-span-2'>
          <p className={`${theme.tailwind.texting2}`}>Description:</p>
          <p className='font-medium text-slate-200 break-words'>
            {transaction.description || '-'}
          </p>
        </div>
      </div>

      {transaction.receipt_no && (
        <p className='mt-2'>
          <span className='px-1 py-0.5 bg-yellow-800/20 text-yellow-400 rounded-md'>
            Receipt# {transaction.receipt_no}
          </span>
        </p>
      )}

      <div
        className={`flex justify-between items-center mt-3 pt-2 border-t ${theme.tailwind.border19}`}
      >
        <span
          className={`px-2 py-1 text-xs font-medium rounded-md ${
            transaction.status === 'Completed'
              ? 'bg-green-800/20 text-green-400'
              : transaction.status === 'pending'
              ? 'bg-yellow-800/20 text-yellow-400'
              : 'bg-red-800/20 text-red-400'
          }`}
        >
          {transaction.status}
        </span>

        <div className='flex space-x-2'>
          <button
            onClick={() => handleEdit(transaction)}
            disabled={isEmployee}
            className={`p-1.5 rounded-lg ${
              isEmployee
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : `${theme.tailwind.bg25} ${theme.tailwind.text} ${theme.tailwind.bgHover13} ${theme.tailwind.hover1}`
            } transition-colors duration-200`}
            title='Edit'
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(transaction._id)}
            disabled={isEmployee}
            className={`p-1.5 rounded-lg ${
              isEmployee
                ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300'
            } transition-colors duration-200`}
            title='Delete'
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  )
  return (
    <div
      className={`${gradient} rounded-xl sm:rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-3 sm:p-4 md:p-6 lg:p-8 min-h-screen sm:min-h-0`}
    >
      <div className='max-w-[1225px] mx-auto space-y-4'>
        {/* Header */}
        <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
          <div>
            {activeSociety && (
              <div className='flex items-center gap-3'>
                <div
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden border-2 ${theme.tailwind.border} shadow-lg ${theme.tailwind.shadow3}`}
                >
                  <img
                    src={
                      activeSociety.society_image || '/api/placeholder/40/40'
                    }
                    alt={activeSociety.name}
                    className='w-full h-full object-cover'
                    onError={e => {
                      e.target.src = '/api/placeholder/40/40'
                    }}
                  />
                </div>
                <div className='text-left'>
                  <p
                    className={`text-xl sm:text-2xl md:text-3xl ${theme.tailwind.text} font-bold truncate`}
                  >
                    {activeSociety.name}
                  </p>
                  <h2
                    className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold ${theme.tailwind.bgr15} bg-clip-text text-transparent`}
                  >
                    Financial Dashboard
                  </h2>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4'>
          {/* Total Income Card */}

          {/* Total Expenses Card */}
          <div
            className={`${theme.tailwind.bgGradient13} backdrop-blur-sm rounded-xl shadow-lg border border-slate-700/50 overflow-hidden group hover:border-red-500/30 transition-all duration-300 hover:shadow-red-500/10`}
          >
            <div className='p-6'>
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <p
                    className={`text-xs sm:text-sm font-medium ${theme.tailwind.texting2} mb-1`}
                  >
                    Total Expenses
                  </p>
                  <p className='text-2xl font-bold bg-gradient-to-r from-red-300 to-red-400 bg-clip-text text-transparent'>
                    {totalExpenses.toLocaleString()} PKR
                  </p>
                </div>
                <div className='p-3 bg-red-500/10 rounded-xl group-hover:bg-red-500/20 transition-all duration-300'>
                  <div className='w-10 h-10 flex items-center justify-center text-red-400 group-hover:text-red-300'>
                    📉
                  </div>
                </div>
              </div>
            </div>
            <div className='h-1 w-full bg-gradient-to-r from-red-500 to-pink-500'></div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className='space-y-3'>
          <div className='flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
            {/* Search Bar */}
            <div className='relative flex-1'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <Search size={18} className={`${theme.tailwind.texting2}`} />
              </div>
              <input
                type='text'
                value={filters.description}
                onChange={e =>
                  setFilters({ ...filters, description: e.target.value })
                }
                placeholder='Search transactions by description...'
                className={`${theme.tailwind.bg14} ${theme.tailwind.text9} ${theme.tailwind.placeHolder3} border ${theme.tailwind.border19} rounded-lg py-2 pl-10 pr-4 w-full focus:ring-2 ${theme.tailwind.focusRing5} ${theme.tailwind.focus6} transition-all duration-200`}
              />
            </div>

            {/* Actions */}
            <div className='flex flex-wrap items-center gap-2'>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center space-x-2 px-4 py-2 ${theme.tailwind.bg5} ${theme.tailwind.bgHover24} ${theme.tailwind.text9} rounded-lg transition-colors duration-200`}
              >
                <Filter size={18} />
                <span>
                  {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
                </span>
              </button>

              <button
                onClick={handleSortOrderChange}
                className={`flex items-center space-x-2 px-4 py-2 ${theme.tailwind.bg5} ${theme.tailwind.bgHover24} ${theme.tailwind.text9} rounded-lg transition-colors duration-200`}
              >
                <ArrowDownUp size={18} />
                <span>
                  {sortOrder === 'asc' ? 'Oldest First' : 'Latest First'}
                </span>
              </button>

              <ExportExcelExpense
                activeSociety={activeSociety}
                filteredTransactions={filteredTransactions}
                filters={filters}
                dispatch={dispatch}
                fetchTransactions={fetchTransactions}
                sortOrder={sortOrder}
              />
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div
              className={`${theme.tailwind.bg14} rounded-xl p-4 border ${theme.tailwind.border19} shadow-lg animate-fadeIn`}
            >
              <div className='flex flex-col md:flex-row md:items-center gap-4'>
                {/* Transaction Direction Filter */}
                <div className='flex-1'>
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.texting1} mb-1`}
                  >
                    Direction
                  </label>
                  <select
                    value='Expense'
                    disabled
                    className={`w-full ${theme.tailwind.bg5} border ${theme.tailwind.border13} ${theme.tailwind.text9} rounded-lg px-3 py-2 focus:ring-2 ${theme.tailwind.focusRing5} ${theme.tailwind.focus6}`}
                  >
                    <option value='Expense'>Expense</option>
                  </select>
                </div>

                {/* Transaction Type Filter */}
                <div className='flex-1'>
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.texting1} mb-1`}
                  >
                    Type
                  </label>
                  <select
                    value={filters.transactionType}
                    onChange={e => {
                      setFilters({
                        ...filters,
                        transactionType: e.target.value
                      })
                      setCurrentPage(1)
                    }}
                    className={`w-full ${theme.tailwind.bg5} border ${theme.tailwind.border13} ${theme.tailwind.text9} rounded-lg px-3 py-2 focus:ring-2 ${theme.tailwind.focusRing5} ${theme.tailwind.focus6}`}
                  >
                    <option value=''>All Types</option>
                    <option value='Full Payment'>Full Payment</option>
                    <option value='Partial Payment'>Partial Payment</option>
                    <option value='Installment Payment'>
                      Installment Payment
                    </option>
                    <option value='Salary Payment'>Salary Payment</option>
                    <option value='Expense Payment'>Expense Payment</option>
                    <option value='Resell Payment'>Resell Payment</option>
                    <option value='Dealer Commission'>Dealer Commission</option>
                    <option value='Booking Full Payment'>
                      Booking Full Payment
                    </option>
                    <option value='Booking Installment Payment'>
                      Booking Installment Payment
                    </option>
                    <option value='Transfer Fee'>Transfer Fee</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className='flex-1'>
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.texting1} mb-1`}
                  >
                    Date Range
                  </label>
                  <EnhancedDateFilter
                    onDateRangeChange={dateRange => {
                      setDateRange(dateRange)
                      // Format dates correctly for API consumption
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        startDate: dateRange[0] ? dateRange[0] : '',
                        endDate: dateRange[1] ? dateRange[1] : ''
                      }))
                      setCurrentPage(1) // Reset to first page when date filter changes
                    }}
                    initialStartDate={filters.startDate}
                    initialEndDate={filters.endDate}
                  />
                </div>

                {/* Clear Filters Button */}
                <div className='self-end py-1'>
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 bg-red-600 hover:bg-red-700 ${theme.tailwind.text9} rounded-lg transition-colors duration-200 flex items-center space-x-1`}
                  >
                    <X size={16} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={`${theme.tailwind.bg2} backdrop-blur rounded-lg sm:rounded-xl border ${theme.tailwind.border2} overflow-hidden`}
        >
          {/* Desktop View */}
          {isTableScrollable ? (
            // Mobile/Tablet View - Card Layout
            <div className='p-3 sm:p-4'>
              {filteredTransactions.length === 0 ? (
                <div className='py-8 text-center'>
                  <Layers
                    className={`h-12 w-12 ${theme.tailwind.text17} mx-auto mb-3`}
                  />
                  <p className={`${theme.tailwind.texting2} text-sm`}>
                    No transactions found
                  </p>
                  <p className={`${theme.tailwind.text17} text-xs mt-1`}>
                    Try adjusting your filters
                  </p>
                </div>
              ) : (
                filteredTransactions.map(transaction =>
                  renderTransactionCard(transaction)
                )
              )}
            </div>
          ) : (
            // Desktop View - Table Layout
            <div className='overflow-x-auto'>
              <div
                className={`p-4 border-b ${theme.tailwind.border19} flex flex-col md:flex-row justify-between md:items-center gap-3`}
              >
                <h3
                  className={`text-lg font-semibold ${theme.tailwind.text9} flex items-center`}
                >
                  <Layers className={`mr-2 h-5 w-5 ${theme.tailwind.text}`} />
                  Expense Transaction History
                </h3>
              </div>
              <table
                className={`min-w-full divide-y ${theme.tailwind.divide1}`}
              >
                <thead>
                  <tr className={`${theme.tailwind.bg3}`}>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Transaction Type
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Name
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Plot No.
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Amount
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      <div
                        className='flex items-center cursor-pointer'
                        onClick={handleSortOrderChange}
                      >
                        Date
                        <ArrowDownUp
                          className={`h-3.5 w-3.5 ml-1 ${
                            sortOrder === 'asc' ? 'rotate-0' : 'rotate-180'
                          } transition-transform duration-300`}
                        />
                      </div>
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Payment Method
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Description
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Status
                    </th>
                    <th
                      className={`px-3 py-3 text-left  text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`bg-slate-850 divide-y ${theme.tailwind.divide}`}
                >
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan='12'
                        className={`px-4 py-8 text-center text-lg ${theme.tailwind.texting2}`}
                      >
                        {transactionsData.loading ? (
                          'Loading transactions...'
                        ) : (
                          <>
                            <div className='flex flex-col items-center justify-center space-y-3'>
                              <div className='text-5xl'>🔍</div>
                              <div className='text-xl font-medium'>
                                No transactions found
                              </div>
                              <p
                                className={`${theme.tailwind.texting2} max-w-md`}
                              >
                                No transactions match your current filters. Try
                                changing your search criteria or clearing
                                filters.
                              </p>
                              {filters.description ||
                              filters.transactionDirection ||
                              filters.transactionType ||
                              filters.startDate ? (
                                <button
                                  onClick={clearFilters}
                                  className={`mt-2 px-4 py-2 ${theme.tailwind.bg18} ${theme.tailwind.bgHover11} ${theme.tailwind.text9} rounded-lg transition-colors duration-200 flex items-center space-x-2`}
                                >
                                  <Filter size={16} />
                                  <span>Clear Filters</span>
                                </button>
                              ) : null}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map(transaction => (
                      <tr
                        key={transaction._id}
                        className={getTransactionTypeColor(
                          transaction.transaction_type,
                          transaction.transaction_direction
                        )}
                      >
                        <td className='px-3 py-3 sm:p-4'>
                          <div className='flex flex-col gap-1'>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-md ${
                                transaction.transaction_direction === 'Income'
                                  ? 'bg-green-700/70 text-green-300'
                                  : 'bg-red-700/70 text-red-300'
                              }`}
                            >
                              {transaction.transaction_direction}
                            </span>
                            <span
                              className={`text-xs font-medium mt-1 ${theme.tailwind.texting2}`}
                            >
                              {transaction.transaction_type}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`px-3 py-3 w-[150px] break-words ${theme.tailwind.text9}`}
                        >
                          {transaction.transaction_type === 'Salary Payment' ? (
                            <>
                              Employee
                              <br />
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                {transaction.employee_id?.name ||
                                  transaction.employee_info?.[0]?.name || (
                                    <i>No Employee</i>
                                  )}
                              </span>
                            </>
                          ) : transaction.transaction_type === '' ? (
                            <>
                              Investor
                              <br />
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                {transaction.users?.investorData?.name || (
                                  <i>No Investor</i>
                                )}
                              </span>
                            </>
                          ) : transaction.transaction_type ===
                            'Dealer Commission' ? (
                            <>
                              Dealer name
                              <br />
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                {transaction.booking_id?.dealer_name ||
                                  transaction.booking_info?.[0]
                                    ?.dealer_name || (
                                    <>
                                      <i className={`${theme.tailwind.text17}`}>
                                        -
                                      </i>
                                      <i>No Dealer</i>
                                    </>
                                  )}
                              </span>
                              <br />
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                {transaction.booking_id?.dealer_phone ||
                                  transaction.booking_info?.[0]
                                    ?.dealer_phone || (
                                    <>
                                      <i className={`${theme.tailwind.text17}`}>
                                        -
                                      </i>
                                      <i>No Phone no</i>
                                    </>
                                  )}
                              </span>
                              <br />
                              <div className={`mt-1 ${theme.tailwind.text9}`}>
                                <span
                                  className={`text-md font-semibold ${theme.tailwind.text9}`}
                                >
                                  <span className='px-1 py-0.5 bg-yellow-800/20 text-yellow-400 text-xs rounded-md'>
                                    plot no#{' '}
                                    {transaction.plot_id?.plot_number ||
                                      transaction.plot_info?.[0]
                                        ?.plot_number || (
                                        <>
                                          <i
                                            className={`${theme.tailwind.text17}`}
                                          >
                                            -
                                          </i>
                                          <i>No Plot no</i>
                                        </>
                                      )}
                                  </span>
                                </span>
                              </div>
                            </>
                          ) : transaction.customer_id?.name ||
                            (transaction.customer_info &&
                              transaction.customer_info[0]?.name) ? (
                            <>
                              Customer
                              <br />
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                {transaction.customer_id?.name ||
                                  transaction.customer_info[0]?.name}
                              </span>
                            </>
                          ) : (
                            <>
                              <span
                                className={`text-md font-semibold ${theme.tailwind.text9}`}
                              >
                                <i>No user</i>
                              </span>
                            </>
                          )}
                          <br />
                          {transaction.transaction_type ===
                            'Dealer Commission' &&
                            transaction.booking_info &&
                            transaction.booking_info[0] &&
                            transaction.booking_info[0]
                              .dealer_commission_date && (
                              <>
                                <span className='px-1 py-1 bg-blue-800/20 text-blue-400 text-xs rounded-md'>
                                  Dealer Date:{' '}
                                  {new Date(
                                    transaction.booking_info[0].dealer_commission_date
                                  ).toLocaleDateString()}
                                </span>
                              </>
                            )}
                        </td>
                        <td
                          className={`px-3 py-3 font-medium ${theme.tailwind.text9}`}
                        >
                          {transaction.plot_id?.plot_number ||
                            (transaction.plot_info &&
                              transaction.plot_info[0]?.plot_number) || (
                              <i className={`${theme.tailwind.text17}`}>-</i>
                            )}
                        </td>
                        <td className='px-3 py-3 font-semibold'>
                          <span
                            className={
                              transaction.transaction_direction === 'Income'
                                ? 'text-emerald-400'
                                : 'text-red-400'
                            }
                          >
                            {transaction.transaction_direction === 'Income'
                              ? '+'
                              : '-'}
                            {transaction.amount?.toLocaleString() || '0'} PKR
                          </span>
                          {transaction.receipt_no && (
                            <div className={`mt-1 ${theme.tailwind.text9}`}>
                              <span className='px-1 py-0.5 bg-yellow-800/20 text-yellow-400 text-xs rounded-md'>
                                Receipt# {transaction.receipt_no}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-3 ${theme.tailwind.text9}`}>
                          {new Date(
                            transaction.transaction_date
                          ).toLocaleDateString('en-GB')}
                        </td>
                        <td className={`px-3 py-3 ${theme.tailwind.text9}`}>
                          {transaction.payment_method || '-'}
                        </td>
                        <td
                          className={`px-3 py-3 w-[150px] break-words ${theme.tailwind.text9}`}
                        >
                          {transaction.description || '-'}
                        </td>
                        <td className={`px-3 py-3 ${theme.tailwind.text9}`}>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-md ${
                              transaction.status === 'Completed'
                                ? 'bg-green-800/20 text-green-400'
                                : transaction.status === 'pending'
                                ? 'bg-yellow-800/20 text-yellow-400'
                                : 'bg-red-800/20 text-red-400'
                            }`}
                          >
                            {transaction.status}
                          </span>
                        </td>
                        <td className={`px-3 py-3 ${theme.tailwind.text9}`}>
                          <div className='flex space-x-2'>
                            <button
                              onClick={() => handleEdit(transaction)}
                              disabled={isEmployee}
                              className={`p-1.5 rounded-lg ${
                                isEmployee
                                  ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                                  : `${theme.tailwind.bg25} ${theme.tailwind.text} ${theme.tailwind.bgHover13} ${theme.tailwind.hover1}`
                              } transition-colors duration-200`}
                              title='Edit'
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(transaction._id)}
                              disabled={isEmployee}
                              className={`p-1.5 rounded-lg ${
                                isEmployee
                                  ? `${theme.tailwind.bg3} ${theme.tailwind.text17} cursor-not-allowed`
                                  : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300'
                              } transition-colors duration-200`}
                              title='Delete'
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {filteredTransactions.length > 0 && (
                  <tfoot className={`${theme.tailwind.bg15}`}>
                    <tr>
                      <td
                        colSpan='6'
                        className={`px-4 py-4 whitespace-nowrap text-sm font-medium text-right ${theme.tailwind.text13}`}
                      >
                        Total Amount:
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <span className={`${theme.tailwind.text9}`}>
                          {totalFilteredAmount <= 0 ? '+' : '-'}
                          {totalFilteredAmount.toLocaleString()} PKR
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredTransactions.length > 0 && renderPagination()}
        </div>
      </div>
    </div>
  )
}

export default ExpenseDirection
